import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { first } from 'lodash';
import { useSnackbar } from 'notistack';
import type { FunctionComponent } from 'react';
import React from 'react';
import { useStore } from 'zustand';

import { useTranslate } from '~/shared/context/use-translate';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuItemIndicator,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import {
    IconArchive,
    IconAsterisk,
    IconCheckmark1Small,
    IconDocument,
    IconExternalLink,
    IconPeopleAdd,
    IconPlus,
    IconSpacer,
} from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';
import type { VersionSelect } from '~/ui/domain/version-select/VersionSelect';
import { theme } from '~/ui/theme/cobrainer-theme';

import { InviteTeamMemberDialog } from '../../company-settings/tabs/organization-tab/users-and-roles/InviteTeamMemberDialog';
import { useInviteTeamMember } from '../api/use-invite-team-member';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { GenericReviewerContextMenuComponent } from '../components/ReviewerComponents';
import { WatchMenuItem } from '../components/WatchMenuItem';
import { useFormattedVersions } from '../hooks/use-formatted-versions';
import { useNavigateToStandaloneProfile } from '../job-profile-standalone/hooks/use-navigate-to-standalone-profile';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { usePdfExport } from '../use-pdf-export';
import { useDraftVersionStore } from './DraftVersionStoreProvider';

const StyledDropdownMenuContent = styled(DropdownMenuContent)({
    zIndex: 3000,
});

const MenuItemWithIcon = styled.span({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
});

const VersionBadge = styled.span({
    display: 'inline-block',
    transform: 'scale(0.8)',
});

type ExportPdfMenuItemProps = {
    readonly item: JobItemDetails;
    readonly version: number;
};

const ExportPdfMenuItem: FunctionComponent<ExportPdfMenuItemProps> = ({ item, version }) => {
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const { exportPdf, isLoading } = usePdfExport({
        jobArchitectureId: item.architectureId,
        jobArchitectureItemId: item.id,
        jobArchitectureItemType: item.type,
        languageCode: activeLanguage,
        version,
    });

    const handleSelect = (event: Event): void => {
        if (isLoading) {
            event.preventDefault();

            return;
        }
        event.preventDefault();
        exportPdf();
    };

    return (
        <DropdownMenuItem disabled={isLoading} onSelect={handleSelect}>
            {isLoading ? <Spinner style={{ color: '#DADAD8' }} /> : <IconDocument size={16} />}
            {translate('pages.job-architecture.export-pdf-button')}
        </DropdownMenuItem>
    );
};

type OpenStandaloneMenuItemProps = {
    readonly item: JobItemDetails;
    readonly onOpenSeparatePage?: () => void;
};

const ShareJobMenuItem: FunctionComponent<{ readonly item: JobItemDetails }> = ({ item }) => {
    const translate = useTranslate();
    const { getStandalonePath } = useNavigateToStandaloneProfile();
    const { enqueueSnackbar } = useSnackbar();

    return (
        <DropdownMenuItem
            onSelect={() => {
                const url = window.location.origin + getStandalonePath(item.architectureId, item.id, item.type);
                navigator.clipboard.writeText(url);
                enqueueSnackbar(translate('pages.job-architecture-v2.job-profile-copied-to-clipboard'), {
                    variant: 'success',
                });
            }}
        >
            <IconExternalLink size={16} />
            {translate('common.share')}
        </DropdownMenuItem>
    );
};

const OpenStandaloneMenuItem: FunctionComponent<OpenStandaloneMenuItemProps> = ({ item, onOpenSeparatePage }) => {
    const translate = useTranslate();
    const { navigate: navigateToStandalone } = useNavigateToStandaloneProfile();
    const { activeLanguage } = useActiveLanguage();

    return (
        <DropdownMenuItem
            onSelect={() => {
                if (is.function(onOpenSeparatePage)) {
                    onOpenSeparatePage();
                    return;
                }
                navigateToStandalone({
                    architectureId: item.architectureId,
                    itemId: item.id,
                    itemType: item.type,
                    preferredLanguage: activeLanguage,
                });
            }}
        >
            <MenuItemWithIcon>
                <IconSpacer />
                {translate('pages.job-architecture-v2.open-separate-page')}
            </MenuItemWithIcon>
        </DropdownMenuItem>
    );
};

type VersionInfo = {
    readonly version: number;
};

const getDraftCompareVersions = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
): readonly [string, string] => {
    const latestVersionNumber = first(versions)?.version ?? 0;

    return [selectedVersion, (latestVersionNumber + 1).toString()];
};

const getExistingCompareVersions = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
): readonly [string, string] | undefined => {
    if (versions.length < 2) {
        return undefined;
    }

    const latestVersion = first(versions);
    if (is.nullOrUndefined(latestVersion)) {
        return undefined;
    }

    const versionToCompare = selectedVersion === latestVersion.version.toString() ? versions[1] : latestVersion;
    if (is.nullOrUndefined(versionToCompare)) {
        return undefined;
    }

    // biome-ignore lint/correctness/useParseIntRadix: suppressed due to migration
    const versionA = Math.min(versionToCompare.version, Number.parseInt(selectedVersion));
    // biome-ignore lint/correctness/useParseIntRadix: suppressed due to migration
    const versionB = Math.max(versionToCompare.version, Number.parseInt(selectedVersion));

    return [versionA.toString(), versionB.toString()];
};

const getCompareVersionPair = (
    selectedVersion: string,
    versions: readonly VersionInfo[],
    hasDraft: boolean,
): readonly [string, string] | undefined => {
    if (hasDraft) {
        return getDraftCompareVersions(selectedVersion, versions);
    }

    return getExistingCompareVersions(selectedVersion, versions);
};

type VersionSubMenuProps = {
    readonly currentVersionId: string | undefined;
    readonly formattedVersions: readonly VersionSelect[];
    readonly isEditing: boolean;
    readonly onVersionChange: (value: string) => void;
};

const VersionSubMenu: FunctionComponent<VersionSubMenuProps> = ({
    currentVersionId,
    formattedVersions,
    isEditing,
    onVersionChange,
}) => {
    const translate = useTranslate();

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={isEditing}>
                <MenuItemWithIcon>
                    <IconSpacer />
                    {translate('pages.job-architecture-v2.job-profile-version-placeholder')}
                </MenuItemWithIcon>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
                style={{
                    zIndex: 3000,
                    maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
                    overflowY: 'auto',
                }}
            >
                <DropdownMenuRadioGroup value={currentVersionId} onValueChange={(value) => onVersionChange(value)}>
                    {formattedVersions.map((version) => (
                        <DropdownMenuRadioItem key={version.id} value={version.id}>
                            <span>
                                {version.title}
                                <VersionBadge>{version.badge}</VersionBadge>
                            </span>
                            <DropdownMenuItemIndicator>
                                <IconCheckmark1Small size={16} />
                            </DropdownMenuItemIndicator>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
};

type ReviewerSubMenuProps = {
    readonly canAssignReviewer: boolean;
    readonly item: JobItemDetails;
    readonly onInvite?: () => void;
};

const ReviewerSubMenu: FunctionComponent<ReviewerSubMenuProps> = ({ canAssignReviewer, item, onInvite }) => {
    const translate = useTranslate();

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={!canAssignReviewer}>
                <MenuItemWithIcon>
                    <IconPeopleAdd size={16} />
                    {translate('pages.job-architecture-v2.job-assign-reviewer')}
                </MenuItemWithIcon>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent style={{ minHeight: '100px', zIndex: 3000 }}>
                <GenericReviewerContextMenuComponent
                    item={item}
                    itemType={item.type}
                    jobArchitectureId={item.architectureId}
                    jobClusterId={item.parentId}
                    onlyAssignReviewer={true}
                    onInvite={onInvite}
                />
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
};

const isValidVersionForCompare = (version: string | undefined): version is string => {
    return !is.nullOrUndefined(version) && !is.emptyString(version);
};

const getCurrentVersionId = (
    isEditing: boolean,
    newTmpVersion: VersionSelect,
    formattedVersions: readonly VersionSelect[],
    selectedVersion: string | undefined,
): string | undefined => {
    if (isEditing) {
        return newTmpVersion.id;
    }

    return formattedVersions.find((v) => v.id === selectedVersion)?.id;
};

type ActionMenuItemsProps = {
    readonly canEdit: boolean;
    readonly isCompareDisabled: boolean;
    readonly isEditingDisabled: boolean;
    readonly isRegenerateDisabled: boolean;
    readonly onCompareVersions: () => void;
    readonly onNewVersion: () => void;
    readonly onRegenerate: () => void;
};

const ActionMenuItems: FunctionComponent<ActionMenuItemsProps> = ({
    isCompareDisabled,
    isEditingDisabled,
    isRegenerateDisabled,
    onCompareVersions,
    onNewVersion,
    onRegenerate,
}) => {
    const translate = useTranslate();

    return (
        <React.Fragment>
            <DropdownMenuItem disabled={isEditingDisabled} onSelect={onNewVersion}>
                <MenuItemWithIcon>
                    <IconPlus size={16} />
                    {translate('pages.job-architecture-v2.job-profile-new-version')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isCompareDisabled} onSelect={() => setTimeout(() => onCompareVersions(), 100)}>
                <MenuItemWithIcon>
                    <IconSpacer />
                    {translate('pages.job-architecture-v2.job-profile-compare-versions')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isRegenerateDisabled} onSelect={onRegenerate}>
                <MenuItemWithIcon>
                    <IconAsterisk size={16} />
                    {translate('pages.job-architecture-v2.job-profile-regenerate')}
                </MenuItemWithIcon>
            </DropdownMenuItem>
        </React.Fragment>
    );
};

type OptionsMenuProps = {
    readonly hasUnsavedChanges: boolean;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly onArchive?: () => void;
    readonly onCompareVersions: (versionA: string, versionB: string, preferredLanguage?: LanguageCodeForJSA) => void;
    readonly onNewVersion: () => void;
    readonly onOpenSeparatePage?: () => void;
    readonly onRegenerate: () => void;
    readonly onVersionChange: (value: string) => void;
    readonly selectedVersion: string | undefined;
};

export const OptionsMenu: FunctionComponent<OptionsMenuProps> = ({
    hasUnsavedChanges,
    isEditing,
    item,
    onArchive,
    onCompareVersions,
    onNewVersion,
    onOpenSeparatePage,
    onRegenerate,
    onVersionChange,
    selectedVersion,
}) => {
    const translate = useTranslate();
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId: item.architectureId,
            itemId: item.id,
            itemType: item.type,
        });
    const { data: versions = [] } = useJobItemVersions({
        architectureId: item.architectureId,
        itemId: item.id,
        itemType: item.type,
    });
    const { formattedVersions, newTmpVersion } = useFormattedVersions(versions);

    const draftStore = useDraftVersionStore();
    const hasDraft = useStore(draftStore, (state) => state.drafts.has(item.id));
    const { activeLanguage } = useActiveLanguage();

    const handleCompareVersions = (): void => {
        if (!isValidVersionForCompare(selectedVersion)) {
            return;
        }

        const versionPair = getCompareVersionPair(selectedVersion, versions, hasDraft);
        if (is.nullOrUndefined(versionPair)) {
            return;
        }

        onCompareVersions(versionPair[0], versionPair[1], activeLanguage);
    };

    const currentVersionId = getCurrentVersionId(isEditing, newTmpVersion, formattedVersions, selectedVersion);
    const canEdit = item.actions?.canEdit === true;
    const canAssignReviewer = item.actions?.canAssignReviewer === true;
    const isEditingDisabled = isEditing || !canEdit;
    const isCompareDisabled = versions.length < 2 && !isEditing;
    const canRegenerate = isEditing && canEdit && hasUnsavedChanges !== true;

    return (
        <React.Fragment>
            <DropdownMenu>
                <DropdownMenuTrigger>
                    <Button size="sm" variant="secondary">
                        {translate('common.options')}
                    </Button>
                </DropdownMenuTrigger>
                <StyledDropdownMenuContent align="end" sideOffset={5}>
                    <ActionMenuItems
                        canEdit={canEdit}
                        isCompareDisabled={isCompareDisabled}
                        isEditingDisabled={isEditingDisabled}
                        isRegenerateDisabled={!canRegenerate}
                        onCompareVersions={handleCompareVersions}
                        onNewVersion={onNewVersion}
                        onRegenerate={onRegenerate}
                    />
                    <ReviewerSubMenu canAssignReviewer={canAssignReviewer} item={item} onInvite={openInviteDialog} />
                    <DropdownMenuSeparator />
                    <VersionSubMenu
                        currentVersionId={currentVersionId}
                        formattedVersions={formattedVersions}
                        isEditing={isEditing}
                        onVersionChange={onVersionChange}
                    />
                    <ExportPdfMenuItem
                        item={item}
                        version={is.string(selectedVersion) ? Number.parseInt(selectedVersion, 10) : 1}
                    />
                    <OpenStandaloneMenuItem item={item} onOpenSeparatePage={onOpenSeparatePage} />
                    <ShareJobMenuItem item={item} />
                    <WatchMenuItem itemId={item.id} />
                    <DropdownMenuSeparator />
                    {!is.undefined(onArchive) && (
                        <DropdownMenuItem disabled={!canEdit} onSelect={onArchive}>
                            <MenuItemWithIcon>
                                <IconArchive size={16} />
                                {translate('pages.job-architecture-v2.archive')}
                            </MenuItemWithIcon>
                        </DropdownMenuItem>
                    )}
                </StyledDropdownMenuContent>
            </DropdownMenu>
            <InviteTeamMemberDialog
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </React.Fragment>
    );
};
